(() => {
  const DESKTOP_QUERY = "(min-width: 769px)";
  const HERO_SECTION_SELECTOR = '[handle="wide-hero"]';
  const HERO_CONTAINER_SELECTOR = '[handle="wide-hero-container"]';
  const HERO_COPY_SELECTOR = '[handle="wide-hero-copy"]';
  const HERO_START_VISIBLE_RATIO = 0.35;
  const HERO_HIDE_DISTANCE_MULTIPLIER = 2;
  const HERO_MIN_HIDE_VIEWPORT_RATIO = 1.25;
  const HERO_PROGRESS_SMOOTHING_MIN = 0.06;
  const HERO_PROGRESS_SMOOTHING_MAX = 0.3;
  const HERO_COMPACT_VIEWPORT_QUERY = "(max-height: 900px)";
  const HERO_COMPACT_HIDE_DISTANCE_MULTIPLIER = 0.65;
  const HERO_COMPACT_MIN_HIDE_VIEWPORT_RATIO = 0.5;
  const HERO_COMPACT_PROGRESS_SMOOTHING_MIN = 0.14;
  const HERO_COMPACT_PROGRESS_SMOOTHING_MAX = 0.38;
  const HERO_COPY_ITEM_SELECTORS = [
    '[handle="wide-hero-1"]',
    '[handle="wide-hero-2"]',
    '[handle="wide-hero-3"]',
    '[handle="wide-hero-4"]',
  ];
  const HERO_COPY_SEQUENCE_START = 0.2;
  const HERO_COPY_ITEM_DURATION = 0.05;
  const HERO_COPY_ITEM_OFFSET = 48;
  const WARMTH_SECTION_SELECTOR = '[handle="wide-warmth"]';
  const WARMTH_COPY_SELECTOR = '[handle="wide-warmth-copy"]';
  const WARMTH_START_VISIBLE_RATIO = 0.5;
  const WARMTH_SCROLL_DISTANCE_RATIO = 0.5;
  const WARMTH_MIN_VIEWPORT_DISTANCE_RATIO = 0.3;
  const WARMTH_PROGRESS_SMOOTHING = 0.2;
  const WARMTH_COMPACT_VIEWPORT_QUERY = "(max-height: 900px)";
  const WARMTH_COMPACT_PROGRESS_SMOOTHING = 0.3;
  const SECTION_SELECTOR = '[handle="wide-system"]';
  const LEFT_SELECTOR = '[handle="container-left"]';
  const RIGHT_SELECTOR = '[handle="container-right"]';
  const TAB_PATTERN = /^October[\u2013-]November-(\d+)$/;
  const SYSTEM_SCROLL_STEP_VIEWPORT_RATIO = 0.5;
  const SYSTEM_SCROLL_STEP_PANEL_RATIO = 0.5;
  const SYSTEM_SCROLL_STEP_MIN = 380;
  const SYSTEM_SCROLL_STEP_MAX = 560;
  const SYSTEM_TAB_SWITCH_INTERVAL = 220;
  const SYSTEM_TAB_SWITCH_THRESHOLD = 0.58;
  const SYSTEM_PANEL_HEIGHT_OFFSET = 120;
  const SYSTEM_TAB_GAP_MAX = 88;
  const SYSTEM_PRODUCT_ENTER_OFFSET = "18%";
  const MOBILE_SYSTEM_EMBLA_DURATION = 55;
  const MOBILE_SYSTEM_EMBLA_MAX_INIT_FRAMES = 120;

  const getIndexedChildren = (container, pattern) =>
    Array.from(container.children)
      .map((element) => {
        const handle = element.getAttribute("handle") || "";
        const match = handle.match(pattern);

        return match
          ? { index: match[1], element }
          : null;
      })
      .filter(Boolean)
      .sort((a, b) => Number(a.index) - Number(b.index));

  const getStyleSnapshot = (element, properties) =>
    properties.reduce((snapshot, property) => {
      snapshot[property] = [
        element.style.getPropertyValue(property),
        element.style.getPropertyPriority(property),
      ];
      return snapshot;
    }, {});

  const restoreStyleSnapshot = (element, snapshot) => {
    Object.entries(snapshot).forEach(([property, [value, priority]]) => {
      if (value) {
        element.style.setProperty(property, value, priority);
      } else {
        element.style.removeProperty(property);
      }
    });
  };

  const initializeHero = (section) => {
    const container = section.querySelector(HERO_CONTAINER_SELECTOR);
    const copy = container?.querySelector(HERO_COPY_SELECTOR);
    const copyItems = copy
      ? HERO_COPY_ITEM_SELECTORS.map((selector) => copy.querySelector(selector))
          .filter(Boolean)
      : [];

    if (!container || !copy || section.dataset.wideHeroInitialized) {
      return;
    }

    const containerImage = getComputedStyle(container)
      .getPropertyValue("--bg-image-pc")
      .trim();
    const copyImage = getComputedStyle(copy)
      .getPropertyValue("--bg-image-pc")
      .trim();

    if (
      !containerImage ||
      containerImage === "none" ||
      !copyImage ||
      copyImage === "none"
    ) {
      return;
    }

    section.dataset.wideHeroInitialized = "true";

    const desktopQuery = window.matchMedia(DESKTOP_QUERY);
    const compactViewportQuery = window.matchMedia(
      HERO_COMPACT_VIEWPORT_QUERY,
    );
    const reducedMotionQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    let currentProgress = 0;
    let targetProgress = 0;
    let measureFrame = 0;
    let renderFrame = 0;
    let hasMeasured = false;

    container.style.setProperty("--wide-hero-original-image", containerImage);
    container.style.setProperty("--wide-hero-copy-image", copyImage);
    container.classList.add("is-wide-hero-ready");

    copyItems.forEach((item) => {
      item.style.setProperty("--wide-hero-copy-item-opacity", "0");
      item.style.setProperty(
        "--wide-hero-copy-item-y",
        HERO_COPY_ITEM_OFFSET + "px",
      );
    });
    copy.classList.add("is-wide-hero-copy-ready");

    const setCopyItemsProgress = (progress) => {
      copyItems.forEach((item, index) => {
        const start =
          HERO_COPY_SEQUENCE_START + index * HERO_COPY_ITEM_DURATION;
        const itemProgress = Math.min(
          1,
          Math.max(0, (progress - start) / HERO_COPY_ITEM_DURATION),
        );
        const easedProgress = 1 - Math.pow(1 - itemProgress, 3);

        item.style.setProperty(
          "--wide-hero-copy-item-opacity",
          easedProgress.toFixed(4),
        );
        item.style.setProperty(
          "--wide-hero-copy-item-y",
          ((1 - easedProgress) * HERO_COPY_ITEM_OFFSET).toFixed(2) + "px",
        );
      });
    };

    const setProgress = (progress) => {
      container.style.setProperty(
        "--wide-hero-reveal-progress",
        (progress * 100).toFixed(4) + "%",
      );
      setCopyItemsProgress(progress);
    };

    const renderProgress = () => {
      renderFrame = 0;

      const difference = targetProgress - currentProgress;

      if (reducedMotionQuery.matches || Math.abs(difference) < 0.001) {
        currentProgress = targetProgress;
      } else {
        const smoothingMin = compactViewportQuery.matches
          ? HERO_COMPACT_PROGRESS_SMOOTHING_MIN
          : HERO_PROGRESS_SMOOTHING_MIN;
        const smoothingMax = compactViewportQuery.matches
          ? HERO_COMPACT_PROGRESS_SMOOTHING_MAX
          : HERO_PROGRESS_SMOOTHING_MAX;
        const smoothing =
          smoothingMin + (smoothingMax - smoothingMin) * currentProgress;

        currentProgress += difference * smoothing;
      }

      setProgress(currentProgress);

      if (currentProgress !== targetProgress) {
        renderFrame = window.requestAnimationFrame(renderProgress);
      }
    };

    const measureProgress = () => {
      measureFrame = 0;

      if (!desktopQuery.matches) {
        currentProgress = 0;
        targetProgress = 0;
        hasMeasured = false;
        setProgress(0);
        return;
      }

      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const isCompactViewport = compactViewportQuery.matches;
      const startTop = viewportHeight - rect.height * HERO_START_VISIBLE_RATIO;
      const hideDistance = Math.max(
        rect.height *
          (isCompactViewport
            ? HERO_COMPACT_HIDE_DISTANCE_MULTIPLIER
            : HERO_HIDE_DISTANCE_MULTIPLIER),
        viewportHeight *
          (isCompactViewport
            ? HERO_COMPACT_MIN_HIDE_VIEWPORT_RATIO
            : HERO_MIN_HIDE_VIEWPORT_RATIO),
        1,
      );
      const rawProgress = (startTop - rect.top) / hideDistance;

      const progress = Math.min(1, Math.max(0, rawProgress));

      targetProgress = reducedMotionQuery.matches
        ? Number(progress > 0)
        : progress;

      if (!hasMeasured) {
        currentProgress = targetProgress;
        hasMeasured = true;
        setProgress(currentProgress);
        return;
      }

      if (!renderFrame) {
        renderFrame = window.requestAnimationFrame(renderProgress);
      }
    };

    const requestMeasure = () => {
      if (!measureFrame) {
        measureFrame = window.requestAnimationFrame(measureProgress);
      }
    };

    const handleViewportChange = () => {
      hasMeasured = false;
      requestMeasure();
    };

    window.addEventListener("scroll", requestMeasure, { passive: true });
    window.addEventListener("resize", handleViewportChange);

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener("change", handleViewportChange);
      reducedMotionQuery.addEventListener("change", requestMeasure);
    } else {
      desktopQuery.addListener(handleViewportChange);
      reducedMotionQuery.addListener(requestMeasure);
    }

    requestMeasure();
  };

  const initializeWarmth = (section) => {
    const copy = section.querySelector(WARMTH_COPY_SELECTOR);
    const directChildren = copy ? Array.from(copy.children) : [];
    const image = directChildren.find(
      (element) => element.getAttribute("handle") === "image",
    );
    const details = directChildren.find(
      (element) => element.getAttribute("handle") === "container",
    );

    if (!copy || !image || !details || section.dataset.wideWarmthInitialized) {
      return;
    }

    section.dataset.wideWarmthInitialized = "true";

    const initialCopyWidth = copy.clientWidth;
    const initialCopyHeight = copy.clientHeight;
    const originalCopyAspectRatio =
      initialCopyWidth > 0 && initialCopyHeight > 0
        ? initialCopyWidth / initialCopyHeight
        : 0;

    const syncCopyHeight = () => {
      if (!originalCopyAspectRatio || !copy.clientWidth) {
        return;
      }

      copy.style.setProperty(
        "--wide-warmth-copy-height",
        (copy.clientWidth / originalCopyAspectRatio).toFixed(2) + "px",
      );
    };

    syncCopyHeight();

    const desktopQuery = window.matchMedia(DESKTOP_QUERY);
    const compactViewportQuery = window.matchMedia(
      WARMTH_COMPACT_VIEWPORT_QUERY,
    );
    const reducedMotionQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    const svgNamespace = "http://www.w3.org/2000/svg";
    const annotation = document.createElementNS(svgNamespace, "svg");
    const focusBox = document.createElementNS(svgNamespace, "path");
    const connector = document.createElementNS(svgNamespace, "path");
    let currentProgress = 0;
    let targetProgress = 0;
    let measureFrame = 0;
    let renderFrame = 0;
    let annotationFrame = 0;
    let hasMeasured = false;

    annotation.classList.add("wide-warmth-annotation");
    annotation.setAttribute("viewBox", "0 0 1 1");
    annotation.setAttribute("preserveAspectRatio", "none");
    annotation.setAttribute("aria-hidden", "true");

    focusBox.classList.add("wide-warmth-focus-box");
    focusBox.setAttribute("pathLength", "1");

    connector.classList.add("wide-warmth-connector");
    connector.setAttribute("pathLength", "1");

    annotation.append(focusBox, connector);
    copy.appendChild(annotation);
    copy.classList.add("is-wide-warmth-ready");

    const updateAnnotationGeometry = () => {
      annotationFrame = 0;

      const copyWidth = copy.clientWidth;
      const copyHeight = copy.clientHeight;

      if (!copyWidth || !copyHeight) {
        return;
      }

      const boxLeft = copyWidth * 0.31;
      const boxTop = copyHeight * (230 / 591);
      const boxRight = boxLeft + copyWidth * 0.11;
      const boxBottom = boxTop + copyHeight * (85 / 591);
      const boxCenterY = (boxTop + boxBottom) / 2;
      const imageLeft = Math.min(copyWidth - 1, image.offsetLeft);
      const imageConnectionY = Math.min(
        copyHeight - 1,
        image.offsetTop + image.offsetHeight * 0.75,
      );
      const format = (value) => value.toFixed(2);

      annotation.setAttribute("viewBox", `0 0 ${copyWidth} ${copyHeight}`);
      focusBox.setAttribute(
        "d",
        [
          `M ${format(boxRight)} ${format(boxCenterY)}`,
          `L ${format(boxRight)} ${format(boxBottom)}`,
          `L ${format(boxLeft)} ${format(boxBottom)}`,
          `L ${format(boxLeft)} ${format(boxTop)}`,
          `L ${format(boxRight)} ${format(boxTop)}`,
          `L ${format(boxRight)} ${format(boxCenterY)}`,
        ].join(" "),
      );
      connector.setAttribute(
        "d",
        [
          `M ${format(boxRight)} ${format(boxBottom)}`,
          `L ${format(imageLeft)} ${format(imageConnectionY)}`,
        ].join(" "),
      );
    };

    const requestAnnotationGeometry = () => {
      if (!annotationFrame) {
        annotationFrame = window.requestAnimationFrame(updateAnnotationGeometry);
      }
    };

    const getStageProgress = (progress, start, end) =>
      Math.min(1, Math.max(0, (progress - start) / (end - start)));

    const easeOutCubic = (progress) => 1 - Math.pow(1 - progress, 3);

    const setElementProgress = (element, progress) => {
      const easedProgress = easeOutCubic(progress);

      element.style.setProperty(
        "--wide-warmth-item-opacity",
        easedProgress.toFixed(4),
      );
    };

    const setStrokeProgress = (element, progress) => {
      if (progress >= 0.9995) {
        element.style.strokeDasharray = "none";
        element.style.strokeDashoffset = "0";
        return;
      }

      element.style.strokeDasharray = "1";
      element.style.strokeDashoffset = (1 - progress).toFixed(4);
    };

    const setProgress = (progress) => {
      const boxProgress = getStageProgress(progress, 0, 0.25);
      const connectorProgress = getStageProgress(progress, 0.25, 0.5);
      const contentProgress = getStageProgress(progress, 0.5, 1);

      setStrokeProgress(focusBox, boxProgress);
      setStrokeProgress(connector, connectorProgress);
      setElementProgress(image, contentProgress);
      setElementProgress(details, contentProgress);
    };

    const renderProgress = () => {
      renderFrame = 0;

      const difference = targetProgress - currentProgress;

      if (reducedMotionQuery.matches || Math.abs(difference) < 0.001) {
        currentProgress = targetProgress;
      } else {
        const smoothing = compactViewportQuery.matches
          ? WARMTH_COMPACT_PROGRESS_SMOOTHING
          : WARMTH_PROGRESS_SMOOTHING;

        currentProgress += difference * smoothing;
      }

      setProgress(currentProgress);

      if (currentProgress !== targetProgress) {
        renderFrame = window.requestAnimationFrame(renderProgress);
      }
    };

    const measureProgress = () => {
      measureFrame = 0;

      if (!desktopQuery.matches) {
        currentProgress = 0;
        targetProgress = 0;
        hasMeasured = false;
        setProgress(0);
        return;
      }

      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const startTop =
        viewportHeight - rect.height * WARMTH_START_VISIBLE_RATIO;
      const scrollDistance = Math.max(
        rect.height * WARMTH_SCROLL_DISTANCE_RATIO,
        viewportHeight * WARMTH_MIN_VIEWPORT_DISTANCE_RATIO,
        1,
      );
      const rawProgress = (startTop - rect.top) / scrollDistance;
      const progress = Math.min(1, Math.max(0, rawProgress));

      targetProgress = reducedMotionQuery.matches
        ? Number(progress > 0)
        : progress;

      if (!hasMeasured) {
        currentProgress = targetProgress;
        hasMeasured = true;
        setProgress(currentProgress);
        return;
      }

      if (!renderFrame) {
        renderFrame = window.requestAnimationFrame(renderProgress);
      }
    };

    const requestMeasure = () => {
      if (!measureFrame) {
        measureFrame = window.requestAnimationFrame(measureProgress);
      }
    };

    const handleViewportChange = () => {
      hasMeasured = false;
      syncCopyHeight();
      requestAnnotationGeometry();
      requestMeasure();
    };

    window.addEventListener("scroll", requestMeasure, { passive: true });
    window.addEventListener("resize", handleViewportChange);

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener("change", handleViewportChange);
      reducedMotionQuery.addEventListener("change", requestMeasure);
    } else {
      desktopQuery.addListener(handleViewportChange);
      reducedMotionQuery.addListener(requestMeasure);
    }

    const imageElement = image.matches("img")
      ? image
      : image.querySelector("img");

    if (imageElement && !imageElement.complete) {
      imageElement.addEventListener("load", requestAnnotationGeometry, {
        once: true,
      });
    }

    requestAnnotationGeometry();
    setProgress(0);
    requestMeasure();
  };

  const initializeSystem = (section) => {
    const left = section.querySelector(LEFT_SELECTOR);
    const right = section.querySelector(RIGHT_SELECTOR);
    const stickyHost = section.closest('.shopify-section') || section;

    if (!left || !right || section.dataset.wideSystemInitialized) {
      return;
    }

    const tabs = getIndexedChildren(right, TAB_PATTERN);
    const panels = getIndexedChildren(
      left,
      /^container-product-(\d+)$/,
    );
    const panelsByIndex = new Map(
      panels.map(({ index, element }) => [index, element]),
    );
    const indexes = tabs
      .map(({ index }) => index)
      .filter((index) => panelsByIndex.has(index));

    if (!indexes.length) {
      return;
    }

    section.dataset.wideSystemInitialized = "true";

    const mediaQuery = window.matchMedia(DESKTOP_QUERY);
    let activeIndex = indexes[0];
    let activePosition = 0;
    let panelHeight = 0;
    let panelHeightCapped = false;
    let scrollFrame = 0;
    let resizeFrame = 0;
    let sectionDocumentTop = 0;
    let interactionHeight = 0;
    let interactionDistance = 0;
    let layoutSignature = "";
    let queuedPosition = activePosition;
    let tabSwitchTimer = 0;
    let lastTabSwitchTime = 0;

    const getScrollStepDistance = (measuredPanelHeight = panelHeight) =>
      Math.round(
        Math.min(
          SYSTEM_SCROLL_STEP_MAX,
          Math.max(
            SYSTEM_SCROLL_STEP_MIN,
            window.innerHeight * SYSTEM_SCROLL_STEP_VIEWPORT_RATIO,
            measuredPanelHeight * SYSTEM_SCROLL_STEP_PANEL_RATIO,
          ),
        ),
      );

    const getPanelHeightLimit = () =>
      Math.max(window.innerHeight - SYSTEM_PANEL_HEIGHT_OFFSET, 1);

    const getPanelTopOffset = (height) =>
      panelHeightCapped
        ? Math.max(window.innerHeight - height, 0)
        : Math.max((window.innerHeight - height) / 2, 0);

    const getLayoutSignature = () =>
      [
        section.clientWidth,
        left.clientWidth,
        right.clientWidth,
        right.scrollHeight,
      ]
        .map(Math.round)
        .join(":");

    const getScrollMetrics = () => {
      const sectionTop = sectionDocumentTop;
      const topOffset = getPanelTopOffset(interactionHeight);

      return {
        start: sectionTop - topOffset,
        distance: Math.max(interactionDistance, 1),
      };
    };

    const syncLeftBackground = () => {
      const activePanel = panelsByIndex.get(activeIndex);
      const color = activePanel?.style
        .getPropertyValue("--bg-color-pc")
        .trim();

      if (color) {
        left.style.setProperty("--wide-system-active-bg", color);
      } else {
        left.style.removeProperty("--wide-system-active-bg");
      }
    };

    const applyActiveState = (index, animate) => {
      const nextPosition = indexes.indexOf(index);

      if (nextPosition < 0) {
        return false;
      }

      const changed = index !== activeIndex;
      const direction = nextPosition >= activePosition ? 1 : -1;

      activeIndex = index;
      activePosition = nextPosition;

      tabs.forEach(({ index: tabIndex, element }) => {
        const isActive = tabIndex === activeIndex;

        element.classList.toggle("is-active", isActive);
        element.setAttribute("aria-selected", String(isActive));
        element.setAttribute("tabindex", isActive ? "0" : "-1");
        element.setAttribute("data-wide-system-tab", tabIndex);
      });

      panels.forEach(({ index: panelIndex, element }) => {
        const isActive = panelIndex === activeIndex;

        element.classList.toggle("is-active", isActive);
        element.classList.toggle(
          "is-entering",
          isActive && animate && changed,
        );
        element.hidden = mediaQuery.matches ? !isActive : false;
        element.setAttribute("aria-hidden", String(!isActive));

        if (isActive && animate && changed) {
          element.style.setProperty(
            "--wide-product-enter-offset",
            direction > 0
              ? SYSTEM_PRODUCT_ENTER_OFFSET
              : "-" + SYSTEM_PRODUCT_ENTER_OFFSET,
          );
        } else {
          element.style.removeProperty("--wide-product-enter-offset");
        }
      });

      syncLeftBackground();
      return changed;
    };

    const measurePanelHeight = (index = activeIndex) => {
      const activePanel = panelsByIndex.get(index);

      if (!activePanel) {
        return 1;
      }

      const leftSnapshot = getStyleSnapshot(left, [
        "height",
        "display",
        "position",
      ]);
      const panelSnapshots = panels.map(({ element }) => ({
        element,
        hidden: element.hidden,
        styles: getStyleSnapshot(element, [
          "position",
          "display",
          "height",
          "min-height",
          "visibility",
          "animation",
        ]),
      }));

      section.classList.remove("is-wide-system-ready");
      section.style.removeProperty("--wide-system-panel-height");
      left.style.setProperty("height", "auto", "important");
      left.style.setProperty("display", "block", "important");

      panels.forEach(({ element }) => {
        element.hidden = true;
        element.style.setProperty("display", "none", "important");
      });

      activePanel.hidden = false;
      activePanel.style.setProperty("position", "relative", "important");
      activePanel.style.setProperty("display", "flex", "important");
      activePanel.style.setProperty("height", "auto", "important");
      activePanel.style.setProperty("min-height", "0", "important");
      activePanel.style.setProperty("visibility", "visible", "important");
      activePanel.style.setProperty("animation", "none", "important");

      void activePanel.offsetHeight;

      const measuredHeight = Math.ceil(
        Math.max(
          activePanel.scrollHeight,
          activePanel.getBoundingClientRect().height,
          activePanel.offsetHeight,
          1,
        ),
      );
      const heightLimit = getPanelHeightLimit();
      panelHeightCapped = measuredHeight > heightLimit;
      const height = Math.min(measuredHeight, heightLimit);
      section.classList.toggle(
        "is-wide-system-height-capped",
        panelHeightCapped,
      );

      restoreStyleSnapshot(left, leftSnapshot);
      panelSnapshots.forEach(({ element, hidden, styles }) => {
        restoreStyleSnapshot(element, styles);
        element.hidden = hidden;
      });

      section.style.setProperty(
        "--wide-system-panel-height",
        height + "px",
      );

      return height;
    };

    const updateRightTabGap = () => {
      const matchedTabs = tabs.filter(({ index }) => indexes.includes(index));
      const gapCount = matchedTabs.length - 1;

      if (gapCount <= 0) {
        right.style.setProperty("--wide-system-tab-gap", "0px");
        return;
      }

      const rightStyle = window.getComputedStyle(right);
      const verticalPadding =
        (parseFloat(rightStyle.paddingTop) || 0) +
        (parseFloat(rightStyle.paddingBottom) || 0);
      const tabsHeight = matchedTabs.reduce(
        (height, { element }) =>
          height + element.getBoundingClientRect().height,
        0,
      );
      const availableGapSpace = Math.max(
        right.clientHeight - verticalPadding - tabsHeight,
        0,
      );
      const gap = Math.min(
        SYSTEM_TAB_GAP_MAX,
        availableGapSpace / gapCount,
      );

      right.style.setProperty("--wide-system-tab-gap", gap + "px");
    };

    const updateStickyOffsets = () => {
      const leftTop = getPanelTopOffset(panelHeight);

      section.style.setProperty(
        "--wide-system-sticky-top",
        leftTop + "px",
      );
      if (stickyHost !== section) {
        stickyHost.style.setProperty(
          "--wide-system-sticky-top",
          leftTop + "px",
        );
        stickyHost.style.setProperty(
          "--wide-system-panel-height",
          panelHeight + "px",
        );
        stickyHost.style.setProperty(
          "--wide-system-scroll-distance",
          interactionDistance + "px",
        );
      }
    };

    const updatePinnedState = (start, distance) => {
      const scrollTop = window.scrollY;
      const isPinned = scrollTop >= start;
      const isWithinRange =
        scrollTop >= start && scrollTop <= start + distance;

      section.classList.toggle('is-wide-system-pinned', isPinned);
      return isWithinRange;
    };

    const updateDesktopLayout = () => {
      if (!mediaQuery.matches) {
        window.clearTimeout(tabSwitchTimer);
        tabSwitchTimer = 0;
        queuedPosition = activePosition;
        section.classList.remove("is-wide-system-ready");
        section.classList.remove("is-wide-system-height-capped");
        section.classList.remove('is-wide-system-pinned');
        if (stickyHost !== section) {
          stickyHost.classList.remove('wide-system-sticky-host');
          stickyHost.classList.remove('is-wide-system-pinned');
          stickyHost.style.removeProperty('--wide-system-sticky-top');
          stickyHost.style.removeProperty('--wide-system-panel-height');
          stickyHost.style.removeProperty('--wide-system-scroll-distance');
        }
        section.style.removeProperty("--wide-system-panel-height");
        section.style.removeProperty("--wide-system-sticky-top");
        right.style.removeProperty("--wide-system-tab-gap");
        panelHeight = 0;
        panelHeightCapped = false;

        panels.forEach(({ element }) => {
          element.classList.remove("is-active");
          element.classList.remove("is-entering");
          element.hidden = false;
          element.removeAttribute("role");
          element.removeAttribute("aria-hidden");
          element.removeAttribute("aria-labelledby");
          element.removeAttribute("data-wide-system-product");
          element.style.removeProperty("--wide-product-enter-offset");
        });

        tabs.forEach(({ element }) => {
          element.classList.remove("is-active");
          element.removeAttribute("role");
          element.removeAttribute("aria-selected");
          element.removeAttribute("aria-controls");
          element.removeAttribute("tabindex");
          element.removeAttribute("data-wide-system-tab");
        });

        right.removeAttribute("role");
        return;
      }

      section.classList.remove('is-wide-system-pinned');
      if (stickyHost !== section) {
        stickyHost.classList.remove('wide-system-sticky-host');
        stickyHost.classList.remove('is-wide-system-pinned');
      }
      sectionDocumentTop =
        section.getBoundingClientRect().top + window.scrollY;
      applyActiveState(activeIndex, false);
      panelHeight = measurePanelHeight(indexes[0]);
      interactionHeight = panelHeight;
      interactionDistance = indexes.length > 1
        ? getScrollStepDistance(panelHeight) * (indexes.length - 1)
        : 0;
      if (stickyHost !== section) {
        stickyHost.classList.add('wide-system-sticky-host');
      }

      section.classList.add("is-wide-system-ready");
      updateRightTabGap();

      panels.forEach(({ index, element }) => {
        const isActive = index === activeIndex;

        element.hidden = !isActive;
        element.setAttribute("role", "tabpanel");
        element.setAttribute("aria-hidden", String(!isActive));
        element.setAttribute("data-wide-system-product", index);

        const tab = tabs.find((item) => item.index === index)?.element;

        if (tab) {
          element.setAttribute(
            "aria-labelledby",
            tab.id || "October-November-" + index,
          );
          tab.setAttribute(
            "aria-controls",
            element.id || "container-product-" + index,
          );
        }
      });

      tabs.forEach(({ index, element }) => {
        element.setAttribute("role", "tab");
        element.setAttribute("aria-selected", String(index === activeIndex));
        element.setAttribute("tabindex", index === activeIndex ? "0" : "-1");
        element.setAttribute("data-wide-system-tab", index);
      });

      right.setAttribute("role", "tablist");
      updateStickyOffsets();
      layoutSignature = getLayoutSignature();
    };

    const finishQueuedSwitch = () => {
      tabSwitchTimer = 0;

      if (!mediaQuery.matches || queuedPosition === activePosition) {
        return;
      }

      const nextPosition = activePosition + Math.sign(
        queuedPosition - activePosition,
      );

      if (applyActiveState(indexes[nextPosition], true)) {
        lastTabSwitchTime = performance.now();
        section.classList.add("is-wide-system-ready");
        updateStickyOffsets();
      }

      if (queuedPosition !== activePosition) {
        tabSwitchTimer = window.setTimeout(
          finishQueuedSwitch,
          SYSTEM_TAB_SWITCH_INTERVAL,
        );
      }
    };

    const queueActivePosition = (position) => {
      queuedPosition = Math.min(
        indexes.length - 1,
        Math.max(0, position),
      );

      if (queuedPosition === activePosition || tabSwitchTimer) {
        return;
      }

      const elapsed = performance.now() - lastTabSwitchTime;
      const delay = Math.max(0, SYSTEM_TAB_SWITCH_INTERVAL - elapsed);

      if (delay === 0) {
        finishQueuedSwitch();
      } else {
        tabSwitchTimer = window.setTimeout(finishQueuedSwitch, delay);
      }
    };

    const getTargetPosition = (scrollPosition) => {
      let position = activePosition;

      while (
        position < indexes.length - 1 &&
        scrollPosition >= position + SYSTEM_TAB_SWITCH_THRESHOLD
      ) {
        position += 1;
      }

      while (
        position > 0 &&
        scrollPosition <= position - SYSTEM_TAB_SWITCH_THRESHOLD
      ) {
        position -= 1;
      }

      return position;
    };

    const updateActiveFromScroll = () => {
      if (!mediaQuery.matches || !panelHeight) {
        return;
      }

      const { start, distance } = getScrollMetrics();

      // The sticky section keeps the visible panel centered for this range.
      const isWithinRange = updatePinnedState(start, distance);

      if (window.scrollY < start) {
        queueActivePosition(0);
        return;
      }

      if (window.scrollY > start + distance) {
        queueActivePosition(indexes.length - 1);
        return;
      }

      if (!isWithinRange) {
        return;
      }

      if (!distance || indexes.length === 1) {
        applyActiveState(indexes[0], false);
        return;
      }

      const stepDistance = distance / (indexes.length - 1);
      const scrollPosition = (window.scrollY - start) / stepDistance;

      queueActivePosition(getTargetPosition(scrollPosition));
    };

    const handleScroll = () => {
      if (scrollFrame) {
        return;
      }

      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = 0;
        updateActiveFromScroll();
      });
    };

    const refreshLayout = () => {
      if (resizeFrame) {
        return;
      }

      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = 0;
        window.clearTimeout(tabSwitchTimer);
        tabSwitchTimer = 0;
        queuedPosition = activePosition;
        if (mediaQuery.matches) {
          updateDesktopLayout();
          updateActiveFromScroll();
        }
      });
    };

    const layoutObserver = typeof ResizeObserver === "function"
      ? new ResizeObserver(() => {
          if (!mediaQuery.matches) {
            return;
          }

          const nextSignature = getLayoutSignature();

          if (nextSignature === layoutSignature) {
            return;
          }

          layoutSignature = nextSignature;
          refreshLayout();
        })
      : null;

    layoutObserver?.observe(section);
    layoutObserver?.observe(left);
    layoutObserver?.observe(right);

    panels.forEach(({ element }) => {
      element.querySelectorAll("img").forEach((image) => {
        if (!image.complete) {
          image.addEventListener("load", refreshLayout, { once: true });
        }
      });
    });

    const scrollToPanel = (index) => {
      if (!mediaQuery.matches) {
        return;
      }

      const position = indexes.indexOf(index);

      if (position < 0) {
        return;
      }

      const { start, distance } = getScrollMetrics();
      const progress = indexes.length > 1
        ? position / (indexes.length - 1)
        : 0;
      const maxScrollTop = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight,
      );
      const target = Math.min(
        maxScrollTop,
        Math.max(0, start + distance * progress),
      );

      window.scrollTo({
        top: target,
        behavior: "smooth",
      });
    };

    tabs.forEach(({ index, element }) => {
      element.addEventListener("click", () => scrollToPanel(index));
      element.addEventListener("keydown", (event) => {
        if (!mediaQuery.matches) {
          return;
        }

        const currentPosition = indexes.indexOf(index);
        let nextPosition = currentPosition;

        if (event.key === "ArrowDown" || event.key === "ArrowRight") {
          nextPosition = Math.min(currentPosition + 1, indexes.length - 1);
        } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
          nextPosition = Math.max(currentPosition - 1, 0);
        } else if (event.key === "Home") {
          nextPosition = 0;
        } else if (event.key === "End") {
          nextPosition = indexes.length - 1;
        } else if (event.key === "Enter" || event.key === " ") {
          scrollToPanel(index);
          event.preventDefault();
          return;
        } else {
          return;
        }

        event.preventDefault();
        scrollToPanel(indexes[nextPosition]);
        tabs.find((item) => item.index === indexes[nextPosition])?.element.focus();
      });
    });

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", refreshLayout);

    const onViewportChange = () => {
      updateDesktopLayout();
      updateActiveFromScroll();
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", onViewportChange);
    } else {
      mediaQuery.addListener(onViewportChange);
    }

    window.addEventListener("load", refreshLayout, { once: true });

    if (document.fonts?.ready) {
      document.fonts.ready.then(refreshLayout);
    }

    updateDesktopLayout();
    updateActiveFromScroll();
  };

  const initializeMobileSystemCarousel = (section) => {
    const carousel = section.querySelector(
      '[handle="container-Mobile"] [data-embla]',
    );
    const viewport = carousel?.querySelector('[data-embla-viewport]');

    if (!carousel || !viewport || carousel.dataset.wideSystemSnapReady) {
      return;
    }

    let frameCount = 0;

    const configureCarousel = () => {
      const instance = window.theme?.EmblasInstances?.find(
        (emblaApi) =>
          typeof emblaApi.rootNode === "function" &&
          emblaApi.rootNode() === viewport,
      );

      if (instance) {
        const playAutoplay = () => {
          instance.plugins().autoplay?.play();
        };

        instance.reInit({
          duration: MOBILE_SYSTEM_EMBLA_DURATION,
          dragFree: false,
          skipSnaps: false,
        });
        playAutoplay();
        instance.on("pointerup", playAutoplay);
        carousel.dataset.wideSystemSnapReady = "true";
        return;
      }

      frameCount += 1;

      if (frameCount < MOBILE_SYSTEM_EMBLA_MAX_INIT_FRAMES) {
        window.requestAnimationFrame(configureCarousel);
      }
    };

    configureCarousel();
  };

  const initialize = (root = document) => {
    const heroSections = root.matches?.(HERO_SECTION_SELECTOR)
      ? [root]
      : root.querySelectorAll(HERO_SECTION_SELECTOR);
    const systemSections = root.matches?.(SECTION_SELECTOR)
      ? [root]
      : root.querySelectorAll(SECTION_SELECTOR);
    const warmthSections = root.matches?.(WARMTH_SECTION_SELECTOR)
      ? [root]
      : root.querySelectorAll(WARMTH_SECTION_SELECTOR);

    heroSections.forEach(initializeHero);
    warmthSections.forEach(initializeWarmth);
    systemSections.forEach((section) => {
      initializeSystem(section);
      initializeMobileSystemCarousel(section);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize(), {
      once: true,
    });
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });
})();
